import {gsap, Observer} from "../utils/vendor.js";
import addGlobalStylesToShadowRoot from "../utils/global-styles.js";

function buildShadowDOM(element) {
    // Prima di partire, esamino il contenuto dell’elemento
    const children = Array.from(element.childNodes)
        .filter(node => {
            // Mantengo solo nodi e testi non vuoti
            return node.nodeType === node.ELEMENT_NODE
                || (node.nodeType === node.TEXT_NODE && node.textContent.trim())
        });

    // Se il custom element è vuoto, posso uscire senza fare nulla
    if (children.length === 0) {
        return false;
    }

    // Shadow DOM
    const shadow = element.attachShadow({mode: "open"});

    shadow.innerHTML = `<style>:host{overflow:hidden;position:relative}.rail{display:flex;flex-direction:row;white-space:nowrap;width:fit-content}.rail>*{align-items:center;display:flex;gap:.25em;white-space:nowrap}</style><div class="rail"></div>`;

    // Aggancio il CSS del sito
    addGlobalStylesToShadowRoot(shadow);

    const rail = shadow.querySelector(".rail");

    // Per non avere spazi vuoti, devo duplicare il contenuto finché
    // non riempie completamente l’elemento. Quindi lo inserisco in un
    // fragment temporaneo e lo appendo al rail finché non ottengo
    // l’effetto desiderato.
    const fragment = document.createDocumentFragment();
    children.forEach(child => {
        // se il child è di tipo testo, lo inserisco in uno <span> (non è possibile animare un testo)
        if (child.nodeType === child.TEXT_NODE) {
            const span = document.createElement("span");
            span.innerHTML = child.textContent;
            fragment.appendChild(span);
        } else {
            fragment.appendChild(child.cloneNode(true));
        }
    });

    // Per calcolare il gap tra i frammenti, ne devo prima inserire uno
    rail.appendChild(fragment.cloneNode(true));

    // calcolo il gap
    const lastChild = rail.childNodes[rail.childNodes.length - 1];
    const gap = parseFloat(element.hasAttribute("gap") ? element.getAttribute("gap") : measureSpaceWidth(lastChild));

    // aggiungo il gap al frammento già aggiunto e ai prossimi
    lastChild.style.paddingLeft = `${gap}px`;
    fragment.childNodes[fragment.childNodes.length - 1].style.paddingLeft = `${gap}px`;

    // appendo un altro fragment
    rail.appendChild(fragment.cloneNode(true));

    // aggiungo altri fragment finché non supero la larghezza del custom element
    while (rail.scrollWidth < this.offsetWidth * 2) {
        rail.appendChild(fragment.cloneNode(true));
    }

    return true;
}

/**
 * Permette di misurare la larghezza del carattere " " (spazio) all’interno dell’elemento passato.
 */
function measureSpaceWidth(element) {
    const span = document.createElement("span");
    span.textContent = " ";
    span.style.visibility = "hidden";
    span.style.whiteSpace = "pre"; // Important: preserva lo spazio
    span.style.position = "absolute"; // Evita di influenzare il layout
    span.style.padding = "0";
    span.style.margin = "0";
    span.style.border = "none";

    element.appendChild(span);
    const width = span.getBoundingClientRect().width;
    span.remove();

    return width;
}

export default class MarqueeWebComponent extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        if (false === buildShadowDOM(this)) {
            return;
        }

        const rail = this.shadowRoot.querySelector(".rail");
        const railWidth = rail.getBoundingClientRect().width;

        /**
         * Timeline principale per l’animazione standard del marquee.
         */
        this.tl1 = gsap.timeline({
            repeat: -1,
            onReverseComplete: () => this.tl1.totalTime(this.tl1.rawTime() + this.tl1.duration() * 100),
        });

        this.tl1.to(rail.children, {
            xPercent: -100,
            duration: railWidth / 300, // 300 pixel al secondo
            ease: "none",
        });

        /**
         * Il "trucco" è usare una seconda timeline per animare il "timeScale" della prima:
         *
         * @link https://gsap.com/docs/v3/GSAP/Timeline/timeScale()
         * "You can even tween the timeScale to gradually slow it down or speed it up."
         *
         * Quindi, utilizzo il plugin Observer per controllare quando si scrolla la pagina e
         * far partire la seconda timeline @link https://gsap.com/docs/v3/Plugins/Observer/
         */
        this.observer = Observer.create({
            /**
             * Questo metodo viene chiamato ogni volta che si scrolla la pagina. Qui viene
             * eseguita la seconda timeline, che ha due tween: uno per accelerare la prima
             * timeline, l’altro per rallentarla.
             *
             * Normalmente i due tween verrebbero eseguiti entrambi ogni volta che si scrolla
             * la pagina, ma nel primo è stato impostato il parametro "overwrite: true" che
             * permette di sovrascrivere tutti i tween precedenti, compreso il secondo.
             *
             * Ecco perché il marquee va veloce finché si scrolla e rallenta quando si ferma
             * lo scroll.
             */
            onChangeY: (self) => {
                let factor = self.deltaY > 0 ? 1 : -1;

                this.tl2 = gsap
                    .timeline()
                    .to(this.tl1, {
                        timeScale: factor, // Più veloce!
                        duration: 0.2, // Quanto ci mette ad andare più veloce
                        overwrite: true, // Sovrascrive gli altri tween, finché non si ferma lo scroll
                    })

                    // Entra in funzione quando fermo lo scroll
                    .to(this.tl1, {
                        timeScale: factor, // Il marquee deve tornare alla velocità originale
                        duration: 1, // Indica quanto ci mette a tornare alla velocità originale
                    }, "+=0.3") // Leggero ritardo di 0,3 secondi
                ;
            }
        });
    }

    disconnectedCallback() {
        this.observer?.kill();
        this.tl1?.kill();
        this.tl2?.kill();
    }
}
